/**
 * Decision dimensions.
 *
 * A candidate is not judged as a whole. It is scored on four dimensions, the model
 * is asked about one dimension at a time, and the total is arithmetic over those
 * scores. Three things follow from that shape, and all three are the point of it:
 *
 * - the model and the deterministic rubric produce the same four numbers, so a
 *   degraded round is ranked by the same definition as an answered one;
 * - the interface can show where a candidate won or lost instead of a single
 *   opaque figure;
 * - the ranking is code, so no model can reorder the result with prose.
 *
 * The definitions live here because both sides read them: the engine scores and
 * ranks with them, and the interface labels and explains with them.
 *
 * Weather is not a dimension but a deduction: bad weather does not make a district
 * worse, it makes an outdoor plan worse, and a district with no outdoor activity
 * planned should not be punished for it.
 *
 * Levels are ordered worst first, which is the direction the provider's score
 * answer uses: the probability weighted position is an index into this list, and
 * the index is rescaled onto 0..10.
 */

export const DECISION_DIMENSION_KEYS = ['commute', 'balance', 'coverage', 'access'] as const

export type DecisionDimensionKey = (typeof DECISION_DIMENSION_KEYS)[number]

/** The four scores of one candidate, each on the same 0..10 scale. */
export interface DimensionScores {
  /** Weighted commuting burden. */
  commute: number
  /** How evenly that burden is spread across departure points. */
  balance: number
  /** How well the district covers the requested activities. */
  coverage: number
  /** Parking and connection convenience. */
  access: number
}

export interface DecisionDimension {
  key: DecisionDimensionKey
  /** Translation key the interface labels the dimension with. */
  labelKey: string
  /** Share of the total, as the design fixes it. */
  weight: number
  /** What the model is asked, phrased so the rubric answers the same question. */
  question: string
  /** Ordered levels, worst first, used as the scoring criteria. */
  levels: readonly string[]
}

/** Levels shared by the dimensions that have no vocabulary of their own. */
const DEGREE_LEVELS = ['很差', '偏差', '一般', '较好', '很好'] as const

export const DECISION_DIMENSIONS: Record<DecisionDimensionKey, DecisionDimension> = {
  commute: {
    key: 'commute',
    labelKey: 'dimension.commute',
    weight: 0.45,
    question: '这个出发地到候选商圈的通勤强度如何？按人数加权的负担越大，评分越低。',
    levels: DEGREE_LEVELS,
  },
  balance: {
    key: 'balance',
    labelKey: 'dimension.balance',
    weight: 0.2,
    question: '各出发地到候选商圈的通勤是否均衡？有人明显比别人远时，评分应降低。',
    levels: DEGREE_LEVELS,
  },
  coverage: {
    key: 'coverage',
    labelKey: 'dimension.coverage',
    weight: 0.25,
    question: '候选商圈内所需活动的地点是否充足？缺少任何一种活动时，评分应降低。',
    levels: DEGREE_LEVELS,
  },
  access: {
    key: 'access',
    labelKey: 'dimension.access',
    weight: 0.1,
    question: '到达是否方便？停车、接驳与步行条件越好，评分越高。',
    levels: DEGREE_LEVELS,
  },
}

/** Points taken off the total when bad weather hits a planned outdoor activity. */
export const WEATHER_PENALTY_POINTS = 1.5

/** A fresh set, used when a candidate could not be scored at all. */
export function emptyDimensionScores(): DimensionScores {
  return { commute: 0, balance: 0, coverage: 0, access: 0 }
}

/** Weighted total, less the weather deduction, clamped onto the 0..10 scale. */
export function totalScore(dimensions: DimensionScores, weatherPenalty: boolean): number {
  const weighted = DECISION_DIMENSION_KEYS.reduce(
    (total, key) => total + dimensions[key] * DECISION_DIMENSIONS[key].weight,
    0,
  )
  const adjusted = weighted - (weatherPenalty ? WEATHER_PENALTY_POINTS : 0)
  return Math.round(Math.min(Math.max(adjusted, 0), 10) * 100) / 100
}
